<mat-card class="block">
  <mat-card-content class="!pt-5">
    <div class="flex items-center gap-2 mb-1">
      <mat-icon>lock</mat-icon>
      <span class="font-medium">{{ 'waters.exclusive-prices.heading' | translate }}</span>
    </div>
    <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">{{ 'waters.exclusive-prices.hint' | translate }}</p>

    @if (isLoading() && !grid()) {
      <div class="py-6 flex justify-center">
        <mat-spinner diameter="32" />
      </div>
    } @else if (hasUnsavedExclusiveChange()) {
      <p class="text-gray-500 dark:text-gray-400 py-2">{{ 'waters.exclusive-prices.save-lake-first' | translate }}</p>
    } @else if (!canEdit()) {
      <p class="text-gray-500 dark:text-gray-400 py-2">
        {{ 'waters.exclusive-prices.not-exclusive-hint' | translate }}
      </p>
    } @else if (loadFailed()) {
      <!-- Distinct from the empty states below: the pricing could not be read, which says nothing about what
           this lake has configured. Nothing retries by itself, so the way out is offered here. -->
      <div class="py-2">
        <p class="text-gray-500 dark:text-gray-400">{{ 'waters.exclusive-prices.load-failed-hint' | translate }}</p>
        <button mat-stroked-button type="button" class="mt-2" (click)="retryLoad()">
          {{ 'waters.exclusive-prices.retry' | translate }}
        </button>
      </div>
    } @else if (!hasPriceLists()) {
      <!-- Nothing can be priced without a price list, so the way out is offered right here rather than
           sending the user off to find it. -->
      <div class="py-2">
        <p class="text-gray-500 dark:text-gray-400">{{ 'waters.exclusive-prices.no-price-lists' | translate }}</p>
        <app-add-price-list-button
          class="inline-block mt-2"
          [waterId]="waterId()"
          [disabled]="isSaving()"
          (created)="onPriceListCreated($event)" />
      </div>
    } @else {
      <div class="flex flex-wrap gap-2 items-center mb-2">
        <app-price-list-select
          [priceLists]="priceLists()"
          [selectedId]="selectedPriceListId()"
          [label]="'waters.exclusive-prices.price-list' | translate"
          [discardTitle]="'waters.exclusive-prices.discard-title' | translate"
          [discardPrompt]="'waters.exclusive-prices.discard-prompt' | translate"
          [hasUnsavedChanges]="dirty()"
          [disabled]="isSaving()"
          (priceListChange)="loadGrid($event)" />

        <app-add-price-list-button
          [compact]="true"
          [waterId]="waterId()"
          [hasUnsavedChanges]="dirty()"
          [disabled]="isSaving() || isLoading()"
          (created)="onPriceListCreated($event)" />

        @if (isLoading()) {
          <mat-spinner diameter="24" class="ml-2" />
        }
      </div>

      @if (!hasSeasons()) {
        <p class="text-gray-500 dark:text-gray-400 py-2">{{ 'waters.exclusive-prices.no-seasons' | translate }}</p>
      } @else if (!hasStayTypes()) {
        <p class="text-gray-500 dark:text-gray-400 py-2">{{ 'waters.exclusive-prices.no-stay-types' | translate }}</p>
      } @else if (gridLoadFailed()) {
        <!-- The cells of the selected price list could not be read, so there is no grid to edit. Without this
             the section would render the price-list dropdown over nothing at all. -->
        <div class="py-2">
          <p class="text-gray-500 dark:text-gray-400">
            {{ 'waters.exclusive-prices.grid-load-failed-hint' | translate }}
          </p>
          <button mat-stroked-button type="button" class="mt-2" (click)="retryGrid()">
            {{ 'waters.exclusive-prices.retry' | translate }}
          </button>
        </div>
      } @else if (canRenderGrid()) {
        @if (showErrors() && hasPartialCells()) {
          <p class="text-red-600 text-sm mt-3">{{ 'waters.exclusive-prices.both-prices-required' | translate }}</p>
        }
        @if (showErrors() && hasOutOfRangeCells()) {
          <p class="text-red-600 text-sm mt-3">{{ 'waters.exclusive-prices.price-out-of-range' | translate }}</p>
        }

        <div class="overflow-x-auto mt-4">
          <table class="exclusive-grid">
            <thead>
              <tr>
                <th class="corner">{{ 'waters.exclusive-prices.season-staytype' | translate }}</th>
                @for (stayType of stayTypeColumns(); track stayType.id) {
                  <th>{{ stayType.name }}</th>
                }
              </tr>
            </thead>
            <tbody>
              @for (season of seasonRows(); track season.id) {
                <tr>
                  <th class="row-head" [attr.data-season]="season.id">{{ season.name }}</th>
                  @for (stayType of stayTypeColumns(); track stayType.id) {
                    <td [class.cell-invalid]="showErrors() && isCellInvalid(season.id, stayType.id)">
                      <mat-form-field subscriptSizing="dynamic" floatLabel="always" class="w-full">
                        <mat-label>{{ 'waters.exclusive-prices.purchase' | translate }}</mat-label>
                        <input
                          matInput
                          type="number"
                          min="0"
                          step="0.01"
                          inputmode="decimal"
                          [value]="getPurchase(season.id, stayType.id)"
                          (input)="setPurchase(season.id, stayType.id, $event)" />
                        <span matTextPrefix>€&nbsp;</span>
                      </mat-form-field>
                      <mat-form-field subscriptSizing="dynamic" floatLabel="always" class="w-full mt-3">
                        <mat-label>{{ 'waters.exclusive-prices.sale' | translate }}</mat-label>
                        <input
                          matInput
                          type="number"
                          min="0"
                          step="0.01"
                          inputmode="decimal"
                          [value]="getSelling(season.id, stayType.id)"
                          (input)="setSelling(season.id, stayType.id, $event)" />
                        <span matTextPrefix>€&nbsp;</span>
                      </mat-form-field>
                      @if (showErrors() && isCellPartial(season.id, stayType.id)) {
                        <div class="mt-1 text-xs text-right text-red-600">
                          {{ 'waters.exclusive-prices.both-prices-required-cell' | translate }}
                        </div>
                      } @else if (showErrors() && isCellInvalid(season.id, stayType.id)) {
                        <div class="mt-1 text-xs text-right text-red-600">
                          {{ 'waters.exclusive-prices.price-out-of-range-cell' | translate }}
                        </div>
                      } @else {
                        @let m = margin(season.id, stayType.id);
                        <div class="mt-1 text-xs text-right text-gray-500 dark:text-gray-400">
                          @if (m !== null) {
                            {{ m | number: '1.1-1' }}{{ 'waters.exclusive-prices.margin-suffix' | translate }}
                          } @else {
                            &mdash;
                          }
                        </div>
                      }
                    </td>
                  }
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    }
  </mat-card-content>
</mat-card>
